import { ReactNode } from 'react';
import { cn } from './utils/cn';
import { useIsMobile, useIsTablet } from '../hooks/useMobile';
import { WORKSPACE_SPACING, getDensityClass } from './Workspace/workspaceSpacing';

interface PanelHeaderProps {
  title: ReactNode;
  children?: ReactNode;
  className?: string;
  contentClassName?: string;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
  tone?: "default" | "contrast";
}

export const PanelHeader = ({
  title,
  children,
  leftContent,
  rightContent,
  className,
  contentClassName,
  tone = "default",
}: PanelHeaderProps) => {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const insetClass = getDensityClass(
    isMobile,
    isTablet,
    WORKSPACE_SPACING.headerInsetX,
  );

  const sizeClasses = cn(
    isMobile && 'h-[52px]',
    isTablet && 'h-[56px]',
    !isMobile && !isTablet && 'h-[64px]',
  );

  const textClasses = cn(
    isMobile && 'text-[15px]',
    !isMobile && 'text-[18px]'
  );

  const paddingClasses = cn(
    isMobile && 'gap-2',
    !isMobile && 'gap-4'
  );
  const toneClasses = tone === "contrast" ? "bg-bg-panel-header" : "bg-bg-secondary";

  return (
    <div
      className={cn(
        'flex items-center justify-between border-b border-border-default shrink-0 select-none z-10',
        'backdrop-blur-sm sticky top-0', // Added sticky/blur as it was used in Editor
        toneClasses,
        sizeClasses,
        className
      )}
    >
      <div
        className={cn(
          "mx-auto flex h-full w-full items-center justify-between",
          insetClass,
          contentClassName,
        )}
      >
        <div className={cn("flex items-center min-w-0", paddingClasses)}>
          {leftContent}
          <h3 className={cn("font-sans font-bold text-text-accent tracking-wide truncate", textClasses)}>
            {title}
          </h3>
        </div>
        <div className={cn("flex items-center", paddingClasses)}>
          {rightContent}
          {children}
        </div>
      </div>
    </div>
  );
};
